import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from './Button';
import type { ButtonSize, ButtonVariant } from './Button';
import { copyToClipboard } from '../../lib/utils';

export interface CopyButtonProps {
  value: string;
  /** Shown next to the icon; omit for an icon-only button. */
  label?: string;
  copiedLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  onCopied?: (ok: boolean) => void;
}

/** Copy-to-clipboard with a two-second confirmation. Used for invite and dev links. */
export function CopyButton({
  value,
  label,
  copiedLabel = 'Copied',
  variant = 'secondary',
  size = 'sm',
  className,
  onCopied,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const copy = async () => {
    const ok = await copyToClipboard(value);
    onCopied?.(ok);
    if (!ok) return;
    setCopied(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const icon = copied ? (
    <Check className="size-3.5 text-success" aria-hidden />
  ) : (
    <Copy className="size-3.5" aria-hidden />
  );

  return (
    <Button
      variant={variant}
      size={label ? size : 'icon-sm'}
      className={className}
      onClick={() => void copy()}
      leftIcon={icon}
      aria-label={label ? undefined : copied ? copiedLabel : 'Copy to clipboard'}
    >
      {label ? (copied ? copiedLabel : label) : null}
    </Button>
  );
}
